import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';
import { jsPDF } from 'jspdf';
import PdfReportDocument from '../components/dashboard/PdfReportDocument';

const REPORT_WIDTH = 1280;
const PAGE_MARGIN_MM = 10;

function waitForFrame() {
  return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

function pxToMm(px) {
  return px * 0.264583;
}

async function captureNodeToCanvas(node) {
  return html2canvas(node, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: REPORT_WIDTH,
  });
}

function addCanvasToPdf(pdf, canvas) {
  const pdfPageWidth = pdf.internal.pageSize.getWidth() - PAGE_MARGIN_MM * 2;
  const pdfPageHeight = pdf.internal.pageSize.getHeight() - PAGE_MARGIN_MM * 2;

  const imgWidthMm = pxToMm(canvas.width);
  const imgHeightMm = pxToMm(canvas.height);
  const renderWidth = pdfPageWidth;
  const renderHeight = (imgHeightMm * renderWidth) / imgWidthMm;

  let remainingHeight = renderHeight;
  let position = 0;

  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', PAGE_MARGIN_MM, PAGE_MARGIN_MM + position, renderWidth, renderHeight);
  remainingHeight -= pdfPageHeight;

  while (remainingHeight > -0.1) {
    position -= pdfPageHeight;
    if (remainingHeight <= 0) break;
    pdf.addPage();
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', PAGE_MARGIN_MM, PAGE_MARGIN_MM + position, renderWidth, renderHeight);
    remainingHeight -= pdfPageHeight;
  }
}

export async function exportDashboardPdf(reportProps, { filenamePrefix = 'venue-intelligence' } = {}) {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.width = `${REPORT_WIDTH}px`;
  host.style.background = '#ffffff';
  host.style.pointerEvents = 'none';
  host.style.zIndex = '-1';
  document.body.appendChild(host);

  const root = createRoot(host);

  try {
    root.render(<PdfReportDocument {...reportProps} />);
    await waitForFrame();
    await waitForFrame();

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pages = Array.from(host.querySelectorAll('[data-pdf-page="true"]'));

    for (let index = 0; index < pages.length; index += 1) {
      const pageNode = pages[index];
      if (index > 0) {
        pdf.addPage();
      }
      const canvas = await captureNodeToCanvas(pageNode);
      addCanvasToPdf(pdf, canvas);
    }

    const today = new Date().toISOString().slice(0, 10);
    pdf.save(`${filenamePrefix}-${today}.pdf`);
  } finally {
    root.unmount();
    host.remove();
  }
}

export default exportDashboardPdf;
